import * as React from "react";
import Link from "next/link";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
} from "@reach/accordion";
import { cx } from "@nju/css";
import { IFtthOffer } from "@nju/data-access-portal-offers";
import { useDecodedToken } from "@nju/data-access-portal-wwt";
import { Icon, IconName } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, BlockText, Button, Markdown, Separator, Text } from "@nju/ui";
import { formatPrice } from "@nju/utils";

interface IFibercoOfferDetailsProps {
  offer: IFtthOffer;
  flow?: "lp" | "onboarding";
  setOffer?: (offerId: string) => void;
  selectedOffer?: string | undefined;
  isLoading?: boolean;
}

export function FibercoOfferDetails({
  offer,
  flow = "lp",
  setOffer,
  selectedOffer,
  isLoading,
}: React.PropsWithChildren<IFibercoOfferDetailsProps>) {
  const [longFeaturesExpanded, setLongFeaturesExpanded] = React.useState(false);
  const installationToken = sessionStorage.getItem("installationToken");
  const decodedToken = useDecodedToken(installationToken);
  const isSfh = !!decodedToken?.address.isSfh;

  return (
    <>
      <Separator size="xs" tone="invisible" />
      <BlockText size="xl" testId="offer-download-speed">
        nju światłowód do {offer.downloadFullText}
      </BlockText>
      <Text
        align="left"
        className="pr-2"
        font="lato"
        size={{
          "@onlyMobile": "keyL",
          "@fromTablet": "keyL",
          "@fromDesktop": "keyXL",
        }}
        testId="offer-price"
        tone="accent"
      >
        {formatPrice(offer.totalRecurringPrice, false)}
      </Text>
      <Text
        align="right"
        font="ttfirs"
        size={{
          "@onlyMobile": "m",
          "@fromTablet": "l",
          "@fromDesktop": "keyS",
        }}
        tone="accent"
        weight="bold"
      >
        zł / mies.
      </Text>
      {flow === "lp" && (
        <BlockText tone="neutral">
          internet w budynku jednorodzinnym:{" "}
          <Text weight="bold">
            +{offer.ftthSfhPrice && formatPrice(offer.ftthSfhPrice)} zł
            miesięcznie
          </Text>
        </BlockText>
      )}
      {flow === "onboarding" && isSfh && (
        <BlockText tone="neutral">
          internet w budynku jednorodzinnym:{" "}
          <Text weight="bold">
            +{offer.ftthSfhPrice && formatPrice(offer.ftthSfhPrice)} zł
            miesięcznie
          </Text>
        </BlockText>
      )}
      <BlockText tone="neutral">
        opłata jednorazowa za instalację:{" "}
        <Text weight="bold">{formatPrice(offer.totalOneTimePrice)} zł</Text>
      </BlockText>
      <div
        className={cx("pt-4", {
          "from-tablet:w-[280px]": flow === "lp",
          "from-tablet:w-[150px]": flow === "onboarding",
        })}
      >
        {flow === "lp" && (
          <Link
            href={portalRoutes.sprawdzDostepnosc.index.get()}
            passHref={true}
          >
            <A
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "m",
              }}
            >
              <Text size="l">sprawdź dostępność</Text>
            </A>
          </Link>
        )}
        {flow === "onboarding" && setOffer && (
          <Button
            data-cy={offer.offerId}
            display="block"
            isLoading={isLoading && selectedOffer === offer.offerId}
            onPress={() => {
              setOffer(offer.offerId);
            }}
            size={{ "@onlyMobile": "s" }}
            variant="primary"
          >
            wybierz
          </Button>
        )}
      </div>
      <Separator size="xs" tone="invisible" />
      <Separator size="s" />
      {offer.shortFeatures.map((offer) => {
        return (
          <div key={offer.name}>
            <div className="flex items-center gap-3 [&>img]:max-w-[300px] [&>img]:w-full">
              <Icon
                height={24}
                name={offer.icon as IconName}
                tone="accent"
                width={27}
              />
              <Markdown as="div" content={offer.description.pl} />
            </div>
            <Separator size="s" />
          </div>
        );
      })}
      <Accordion
        collapsible={true}
        onChange={() => {
          return setLongFeaturesExpanded(!longFeaturesExpanded);
        }}
      >
        <AccordionItem>
          <AccordionButton className="flex items-center cursor-pointer bg-transparent border-none underline">
            <Text>
              {longFeaturesExpanded ? "zwiń" : "rozwiń"} dodatkowe informacje
            </Text>
          </AccordionButton>
          <Separator size="s" tone="invisible" />
          {(offer.ftthBundleDiscount || offer.bundleDiscount) && (
            <>
              <div className="border-1 border-cerulean border-dashed rounded-[12px] p-3 mt-4 flex gap-5 max-w-[555px]">
                <div className="w-6 h-6">
                  <Icon
                    height={24}
                    name="badge-percent"
                    tone="accent"
                    width={24}
                  />
                </div>
                <Text size="s">
                  W pakiecie taniej - jeśli masz już aktywną usługę nju
                  subskrypcja, zapłacisz za nią o{" "}
                  {offer.ftthBundleDiscount || offer.bundleDiscount} zł mniej
                  miesięcznie.
                </Text>
              </div>
              <Separator size="s" tone="invisible" />
            </>
          )}
          <AccordionPanel>
            {offer.longFeatures.map((offer) => {
              return (
                <div
                  className="prose prose-h3:text-size-16 prose-h3:font-bold"
                  key={offer.name}
                >
                  {offer.title && (
                    <Text size="keyS" tone="accent">
                      {offer.title.pl}
                    </Text>
                  )}
                  <Separator size="xs" tone="invisible" />
                  <Markdown
                    as="div"
                    className="[&>p]:m-0 [&>h3]:mt-1 [&>h3]:mb-1 [&>ul]:m-0 [&>ul>li]:m-0"
                    content={offer.description.pl}
                  />
                  <Separator size="s" tone="invisible" />
                </div>
              );
            })}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </>
  );
}
